import { directive, apply } from 'twind';
import { css } from 'twind/css';
import deepmerge from 'deepmerge';

const sanitizeInput = (input, theme) => {
    const result = theme('aspectRatio', `${input}`);
    if (input === 'none')
        return input;
    if (typeof result === 'number')
        return result;
    return parseFloat(`${result}`);
};
const aspectRatio$ = (ratio, { theme }) => {
    const value = sanitizeInput(ratio, theme);
    if (!value)
        return '';
    if (value === 'none') {
        return apply `static pb-0 children:(static h-auto w-auto inset-auto)`;
    }
    return {
        width: '100%',
        paddingTop: `${value * 100}%`,
        position: 'relative',
        '& > *': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
        },
    };
};
const aspectRatio = ((parts) => {
    const input = Array.isArray(parts) ? parts[0] : parts;
    return directive(aspectRatio$, input);
});
/**
 * Function for Minimum Aspect Ratio
 * first sanitize the input value and either
 * return styles for 'none'
 * or an aspect ratio value
 */
const minAspectRatio$ = (ratio, { theme }) => {
    const value = sanitizeInput(ratio, theme);
    // if no value, don't return any styles
    if (!value)
        return '';
    // clear styles
    if (value === 'none') {
        return {
            width: 'auto',
            position: 'static',
            '&:before': {
                content: 'none',
                paddingBottom: 0,
                float: 'none',
            },
            '&:after': {
                content: 'none',
            },
        };
    }
    //
    return {
        width: '100%',
        position: 'relative',
        '&:before': {
            content: '""',
            paddingBottom: `${value * 100}%`,
            float: 'left',
        },
        '&:after': {
            display: 'table',
            content: '""',
            clear: 'both',
        },
    };
};
const minAspectRatio = ((parts) => {
    const input = Array.isArray(parts) ? parts[0] : parts;
    return directive(minAspectRatio$, input);
});

const heading$ = (size, { theme }) => {
    const fontSize = theme('headingSize')[size || 'base'];
    return typeof fontSize === 'string'
        ? { fontSize: fontSize }
        : { fontSize: fontSize[0], lineHeight: fontSize[1] };
};
const heading = ((size, context) => Array.isArray(size)
    ? heading$(size[0], context)
    : directive(heading$, `${size}`));

const spacing = {
    px: '1px',
    0: '0px',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    11: '2.75rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem',
    112: '28rem',
    128: '32rem',
};
const textDecorationLineTypes = [
    'underline',
    'overline',
    'line-through',
    'none',
];

/**
 * TODO: focus-visible mismatch
 *
 *      body:not(.focus-visible) * {
 *        outline: none !important;
 *      }
 *
 *      .focus-visible :focus {
 *        outline: none !important;
 *        @apply ring-gray-500 ring-2 rounded-sm;
 *      }
 *
 *      .focus-visible input:focus + [data-type='check-btn'] {
 *        @apply ring-gray-500 ring-2 ring-opacity-90;
 *      }
 *
 *      .focus-visible input:focus + [data-type='radio-btn'] {
 *        @apply ring-gray-500 ring-2 ring-opacity-90;
 *      }
 *
 *      .focus-visible [data-test-id='dropdown-select'] {
 *        @apply focus-within:ring-2 ring-gray-500;
 *      }
 *
 *      .focus-visible input:focus,
 *      .focus-visible textarea:focus {
 *        outline: none !important;
 *        @apply ring-gray-500 ring-2 rounded-sm;
 *      }
 *
 */
const baseConfig = {
    mode: 'silent',
    preflight: `
    html,
    body {
      width: 100%;
    }

    body {
      min-height: 100vh;
      overflow-x: hidden;
      @apply font-display;
      font-kerning: normal;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }


    [data-live-announcer="true"] {
      @apply sr-only;
    }


    /* custom fonts JP */
    body.ja,
    body.ja .font-display {
      font-family: "Noto Sans JP", FFDINforPuma, sans-serif;
    }

    a,button {
      cursor: pointer;
    }

    /* global focus indicator */
    body button:focus-visible, body a:focus-visible {
      outline: 2px solid #8c9198;
      outline-offset: 2px;
    }

    body button:focus:not(:focus-visible), body a:focus:not(:focus-visible) {
      outline: none;
    }

    :not(button):not(a):not(input):not(textarea):not(select):focus-visible {
      outline: 2px solid #8c9198;
      outline-offset: -2px;
    }

    :not(button):not(a):not(input):not(textarea):not(select):focus-visible {
      outline: 2px solid #8c9198;
      outline-offset: -2px;
    }

    .overflow-y-scroll,
    .overflow-y-auto {
      -webkit-overflow-scrolling: touch;
    }

    body *:not([role='dialog']):not(#modal)::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 6px;
      height: 6px;
    }

    body *:not([role='dialog']):not(#modal)::-webkit-scrollbar-track {
      background: #DFE0E1;
      border-radius: 6px;
    }

    body *:not(dialog)::-webkit-scrollbar-thumb {
      background-color: #50565E;
      border-radius: 6px;
    }

    /* input search decorators */
    input[type='search'] {
      -webkit-appearance: none;
    }

    /* Chrome, Safari */
    input[type='search']::-webkit-search-decoration,
    input[type='search']::-webkit-search-cancel-button,
    input[type='search']::-webkit-search-results-button,
    input[type='search']::-webkit-search-results-decoration {
      display: none;
    }

    /* IE, Edge */
    input[type='search']::-ms-clear {
      display: none;
      width: 0;
      height: 0;
    }
  `,
    theme: {
        screens: {
            _: '0px',
            '2xs': '375px',
            xs: '480px',
            sm: '640px',
            md: '768px',
            lg: '1024px',
            xl: '1280px',
            '2xl': '1440px',
            '3xl': '1600px',
            '4xl': '1920px',
            mobile: { raw: 'screen and (max-width: 539px)' },
            tablet: {
                raw: 'screen and (min-width: 540px) and (max-width: 1023px)',
            },
            desktop: { raw: 'screen and (min-width: 1024px)' },
        },
        colors: {
            transparent: 'transparent',
            current: 'currentColor',
            inherit: 'inherit',
            white: '#ffffff',
            black: '#191919',
            gray: {
                100: '#f3f4f6',
                200: '#e5e7eb',
                300: '#d1d5db',
                400: '#9ca3af',
                500: '#6b7280',
                600: '#4b5563',
            },
            red: {
                DEFAULT: '#ba2b20',
                500: '#ef4444',
                600: '#dc2626',
                700: '#b91c1c',
            },
            green: {
                DEFAULT: '#008626',
                500: '#10b981',
                600: '#059669',
            },
            blue: {
                700: '#1d4ed8',
            },
            neutral: {
                DEFAULT: '#191919',
                0: '#ffffff',
                10: '#F6F7F8',
                20: '#DFE0E1',
                30: '#BDC1C5',
                40: '#A1A8AF',
                50: '#8C9198',
                60: '#676D75',
                70: '#50565E',
                80: '#3B4047',
                90: '#262A30',
                100: '#191919',
            },
            outline: {
                DEFAULT: '#8C9198',
                invert: '#676D75',
            },
            brand: {
                DEFAULT: '#AC1E1E',
                0: '#FFF5F5',
                5: '#FCDEDE',
                10: '#F8C6C6',
                20: '#F19595',
                30: '#E66B6B',
                40: '#D84949',
                50: '#C43030',
                60: '#AC1E1E',
                70: '#920F0F',
                80: '#710808',
                90: '#4C0303',
                100: '#260000',
            },
            gold: {
                DEFAULT: '#6D5527',
                80: '#4C3B1B',
                60: '#6D5527',
                40: '#A78B55',
                light: '#A3885F',
            },
            info: {
                DEFAULT: '#0656B6',
                0: '#F5FAFF',
                5: '#DAEAFD',
                10: '#BFDAFB',
                20: '#8ABAF5',
                30: '#5B9CEC',
                40: '#3582DF',
                50: '#196ACD',
                60: '#0656B6',
                70: '#004599',
                80: '#003576',
                90: '#00234F',
                100: '#001126',
            },
            warning: {
                DEFAULT: '#CD9719',
                0: '#FFFBF5',
                5: '#FDF1DA',
                10: '#FBE6BF',
                20: '#F5D18A',
                30: '#ECBD5B',
                40: '#DFAA35',
                50: '#CD9719',
                60: '#B68206',
                70: '#996D00',
                80: '#765400',
                90: '#4F3800',
                100: '#261B00',
            },
            success: {
                DEFAULT: '#4D7D04',
                0: '##FFFFF5',
                5: '#F3F4D2',
                10: '#E4E9B1',
                20: '#C4D477',
                30: '#A1BE49',
                40: '#81A828',
                50: '#649312',
                60: '#4D7D04',
                70: '#3B6700',
                80: '#2D5200',
                90: '#203C00',
                100: '#142600',
            },
            error: {
                DEFAULT: '#B62906',
                0: '#FFF6F5',
                5: '#FDDDDA',
                10: '#FBC5BF',
                20: '#F5988A',
                30: '#EC715B',
                40: '#DF5335',
                50: '#CD3B19',
                60: '#B62906',
                70: '#991F00',
                80: '#761900',
                90: '#4F1100',
                100: '#260800',
            },
        },
        borderColor: theme => ({
            ...theme('colors'),
            DEFAULT: theme('colors.gray.200', 'currentColor'),
        }),
        ringColor: theme => ({
            DEFAULT: '#8C9198',
            invert: '#676D75',
            ...theme('colors'),
        }),
        ringOpacity: {
            DEFAULT: '1',
        },
        ringWidth: {
            DEFAULT: '3px',
            0: '0px',
            1: '1px',
            2: '2px',
            3: '3px',
        },
        fontFamily: {
            display: [
                'FFDINforPuma',
                'Inter',
                '-apple-system',
                'system-ui',
                'BlinkMacSystemFont',
                'Segoe UI',
                'Roboto',
                'Helvetica Neue',
                'Arial',
                'sans-serif',
            ],
        },
        headingSize: {
            xxxs: ['14px', '1.333'],
            xxs: ['16px', '1.333'],
            xs: ['18px', '1.333'],
            sm: ['20px', '1.2'],
            base: ['24px', '1.1666'],
            lg: ['28px', '1.142'],
            xl: ['32px', '1.125'],
            '2xl': ['40px', '1.1'],
            '3xl': ['48px', '1'],
            '4xl': ['60px', '1'],
            '5xl': ['72px', '1'],
            '6xl': ['96px', '1'],
            '7xl': ['112px', '1'],
            inherit: 'inherit',
        },
        fontSize: {
            xxs: ['10px', { lineHeight: '1.6', letterSpacing: '0.04' }],
            xs: ['12px', { lineHeight: '1.333', letterSpacing: '0.04' }],
            sm: ['14px', '1.428'],
            base: ['16px', '1.5'],
            lg: ['18px', '1.5555'],
            xl: ['20px', '1.4'],
            '2xl': ['24px', '1.333'],
            '3xl': ['28px', '1.142'],
            '4xl': ['32px', '1.125'],
            '5xl': ['40px', '1.1'],
            '6xl': ['48px', '1'],
            '7xl': ['60px', '1'],
            '8xl': ['72px', '1'],
            '9xl': ['96px', '1'],
            '10xl': ['112px', '1'],
            inherit: 'inherit',
        },
        fontWeight: {
            normal: '400',
            bold: '700',
            unset: 'unset',
        },
        lineHeight: {
            xxs: '1rem',
        },
        outline: {
            default: ['1px solid #8C9198', '2px'],
            invert: ['1px solid #676D75', '2px'],
            none: ['none', '0px'],
        },
        letterSpacing: {
            tightest: '-.06em',
            tighter: '-.04em',
            tight: '-.02em',
            normal: '0',
            wide: '.02em',
            wider: '.04em',
            widest: '.06em',
        },
        animation: {
            'fade-in': 'fadeIn 200ms',
            'fade-in-up': 'fadeInUp 200ms',
            'fall-from-top': 'fallFromTop 200ms',
            'slide-in-top': 'slideInTop 200ms',
            'slide-in-bottom': 'slideInBottom 200ms',
            none: 'none',
        },
        keyframes: {
            pulse: {
                '0%, 100%': { opacity: '0%' },
                '50%': { opacity: '20%' },
            },
            fadeIn: {
                '0%': { opacity: '0%' },
                '100%': { opacity: '100%' },
            },
            fadeInUp: {
                '0%': { opacity: '0%', transform: 'translateY(2%)' },
                '100%': { opacity: '100%', transform: 'translateY(0)' },
            },
            fallFromTop: {
                from: { opacity: 0, transform: 'translateY(-100%) rotate(-45deg)' },
                to: { opacity: 1, transform: 'translateY(0) rotate(0deg)' },
            },
            slideInTop: {
                from: { transform: 'translateY(-2%)' },
                to: { transform: 'translateY(0)' },
            },
            slideInBottom: {
                from: { transform: 'translateY(2%)' },
                to: { transform: 'translateY(0)' },
            },
        },
        extend: {
            gridAutoColumns: {
                '1': '100%',
                '2': '50%',
                '3': '33.3%',
                '4': '25%',
                '5': '20%',
                '6': '16.666%',
            },
            zIndex: {
                dialog: '1000',
                search: '999',
            },
            borderWidth: {
                1: '1px',
                2: '2px',
                3: '3px',
                4: '4px',
                5: '5px',
                6: '6px',
                8: '8px',
            },
            maxWidth: { ...spacing, min: 'min-content', max: 'max-content' },
            minWidth: { ...spacing, min: 'min-content', max: 'max-content' },
            minHeight: { ...spacing },
            maxHeight: { ...spacing },
            height: {
                max: 'max-content',
            },
            width: {
                112: '28rem',
                128: '32rem',
            },
            boxShadow: {
                'glow-base': '0px 0px 25px -5px rgba(25, 25, 25, 0.15)',
                'glow-success': '0px 0px 25px -5px rgba(32, 60, 0, 0.15)',
                'glow-error': '0px 0px 25px -5px rgba(118, 25, 0, 0.15)',
                'glow-warning': '0px 0px 25px -5px rgba(79, 56, 0, 0.15)',
                'glow-info': '0px 0px 25px -5px rgba(0, 53, 118, 0.15)',
                'glow-base-invert': '0px 0px 25px -5px rgba(223,224,225, 0.15)',
                'glow-success-invert': '0px 0px 25px -5px rgba(196,212,119, 0.15)',
                'glow-error-invert': '0px 0px 25px -5px rgba(251,197,191, 0.15)',
                'glow-warning-invert': '0px 0px 25px -5px rgba(245,209,138, 0.15)',
                'glow-info-invert': '0px 0px 25px -5px rgba(191,218,251, 0.15)',
            },
            transitionTimingFunction: {
                'in-expo': 'cubic-bezier(0.95, 0.05, 0.795, 0.035)',
                'out-expo': 'cubic-bezier(0.19, 1, 0.22, 1)',
            },
        },
    },
    plugins: {
        'font-stretch-normal': css `
      font-stretch: normal;
    `,
        'font-stretch-condensed': css `
      font-stretch: condensed;
    `,
        // heading typescale
        heading: heading,
        // Aspect ratio padding hack
        'aspect-ratio': aspectRatio,
        'min-aspect-ratio': minAspectRatio,
        // css aspect ratio
        aspect: (parts) => ({
            '@supports (aspect-ratio: 1 / 1)': {
                aspectRatio: `${parts[0]} / ${parts[1]}`,
            },
            '@supports not (aspect-ratio: 1 / 1)': {
                '&::before': {
                    content: '""',
                    float: 'left',
                    paddingTop: `calc(100% / (${parts[0] / parts[1]}))`,
                },
                '&::after': {
                    clear: 'left',
                    content: '""',
                    display: 'block',
                },
            },
        }),
        indent: parts => ({
            textIndent: parts[0],
        }),
        content: () => ({
            content: '""',
        }),
        // Text decoration extended types for line, thickeness and color
        decoration: (parts, { theme }) => {
            if (textDecorationLineTypes.includes(parts[0])) {
                return {
                    textDecorationLine: parts[0],
                };
            }
            if (Number(parts[0])) {
                return {
                    textDecorationThickness: theme('borderWidth', parts[0]),
                };
            }
            return {
                textDecorationColor: theme('colors', parts),
            };
        },
        // Text underline offset
        'underline-offset': (parts, { theme }) => ({
            textUnderlineOffset: theme('spacing', parts[0]),
        }),
        'flex-gap': (parts, { theme }) => {
            return {
                marginBottom: `-${theme('spacing', parts)} !important`,
                marginRight: `-${theme('spacing', parts)} !important`,
                '& > *': {
                    marginBottom: `${theme('spacing', parts)} !important`,
                    marginRight: `${theme('spacing', parts)} !important`,
                },
            };
        },
        'line-clamp': parts => {
            return {
                display: '-webkit-box',
                WebkitLineClamp: parts[0],
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
            };
        },
        'no-scrollbars': () => {
            return css `
        &::-webkit-scrollbar {
          width: 0px;
          height: 0px;
        }
      `;
        },
    },
    variants: {
        ltr: '&[dir=ltr], [dir=ltr] &',
        rtl: '&[dir=rtl], [dir=rtl] &',
    },
};

const mergeThemeOptions = {
    isMergeableObject: n => {
        return !!n && typeof n === 'object';
    },
    arrayMerge: (target, source) => source,
};
const mergeConfig = (target, source, options) => {
    const { preflight: targetPreflight = ``, ...targetRest } = target;
    const { preflight: sourcePreflight = ``, ...sourceRest } = source;
    return deepmerge({
        preflight: `${targetPreflight} ${sourcePreflight}`,
        ...targetRest,
    }, sourceRest, options || mergeThemeOptions);
};

const createConfig = (theme) => {
    const { preflight, ...rest } = mergeConfig(baseConfig, theme || {});
    const twindConfig = {
        preflight: twindPreflight => {
            return css `
        ${twindPreflight}
        ${preflight}
      `;
        },
        ...rest,
    };
    return twindConfig;
};

const twindConfig = createConfig();

export { aspectRatio, baseConfig, createConfig, heading, mergeConfig, minAspectRatio, twindConfig };
//# sourceMappingURL=index.js.map
